import "./PlanItem.scss";

import { Button, Flex, InfoIcon, Text, Tooltip } from "@fluentui/react-northstar";
import { TeamsSubscriptionPlans } from "@teams/types";
import { UserDTO } from "@teams/types/api";
import React from "react";

import { getPlanLabel } from "../../../types/subscriptionHelper";
import { PollyIcon } from "../../PollyIcon";
import { TrialRequestButton } from "./components/TrialRequestButton";

type PlanItemProps = {
  background: string;
  price: React.ReactNode;
  plan: TeamsSubscriptionPlans;
  priceTooltip: React.ReactNode;
  description?: React.ReactNode;
  onPlanSelected: (
    plan: TeamsSubscriptionPlans,
    isTrialRequest?: boolean
  ) => Promise<string | undefined>;
  onPlanLevelSelected?: (id: string) => void;
  features: Array<[string, boolean]>;
  primaryColor: string;
  billingFrequency: "monthly" | "yearly";
  user: UserDTO;
  isTrialMode?: boolean;
  trialDays: number;
  footer?: React.ReactNode;
};

export const PlanItem: React.FC<PlanItemProps> = ({
  background,
  plan,
  priceTooltip,
  price,
  features,
  onPlanSelected,
  primaryColor,
  billingFrequency,
  description,
  footer,
  isTrialMode,
  trialDays,
  user,
}) => {
  let frequencyLabel: string;
  switch (billingFrequency) {
    case "monthly":
      frequencyLabel = "Billing Monthly";
      break;
    case "yearly":
      frequencyLabel = "Billing Yearly";
      break;
  }

  const hasPlan = user.subscriptions.some(
    sub => sub.state === "active" && sub.plan === plan && sub.type === "full"
  );

  return (
    <Flex
      className="plan-item"
      style={{ border: "1px solid var(--stardustui-colorScheme-default-border)", width: "100%" }}
      column
    >
      <Flex.Item grow={1}>
        <div>
          <Flex
            styles={{ backgroundImage: `url(${background})` }}
            className="plan-item__plan-header"
            hAlign="center"
            vAlign="center"
            column
          >
            <Text content={getPlanLabel(plan)} size="larger" color="white" as="div" weight="bold" />
            <Tooltip
              content={priceTooltip}
              trigger={
                <Flex vAlign="start">
                  <Text content={price} color="white" weight="semibold" size="medium" />
                  <InfoIcon
                    size="smaller"
                    outline
                    styles={{ color: "white", marginLeft: ".25rem" }}
                  />
                </Flex>
              }
            />
            <Text content={frequencyLabel} color="white" as="div" weight="bold" size="large" />
          </Flex>

          <div className="plan-item__plan-select-container">
            {isTrialMode && (
              <TrialRequestButton
                plan={plan}
                trialLength={trialDays}
                primaryColor={primaryColor}
                onTrialRequested={plan => onPlanSelected(plan, true)}
              />
            )}

            <Button
              className="plan-item__plan-select-button"
              text={isTrialMode === true}
              variables={{
                height: 40,
                backgroundColor: isTrialMode ? undefined : primaryColor,
                color: "white",
              }}
              content={
                hasPlan ? (
                  "Current Plan"
                ) : isTrialMode ? (
                  <Text content="or Purchase Now" styles={{ color: primaryColor }} weight="bold" />
                ) : (
                  `Choose ${getPlanLabel(plan)} Plan`
                )
              }
              fluid
              disabled={hasPlan}
              onClick={() => onPlanSelected(plan)}
            />
          </div>

          {description && (
            <Text className="plan-item__description" content={description} align="center" />
          )}

          <Flex hAlign="center" className="plan-item__featuremap" column>
            <Text content="Includes:" size="large" align="center" as="div" weight="bold" />

            <div>
              {features.map(([label, value]) => (
                <div key={label} style={{ marginTop: ".5rem" }}>
                  <PollyIcon
                    icon={value ? "included-icon" : "not-included-icon"}
                    inheritColor
                    className={value ? "plan-item__plan_checked" : "plan-item__plan_unchecked"}
                  />

                  <Text
                    styles={{ marginLeft: "1rem" }}
                    content={label}
                    weight="bold"
                    size="medium"
                  />
                </div>
              ))}
            </div>
          </Flex>
        </div>
      </Flex.Item>

      {footer && (
        <Flex.Item push>
          <div>{footer}</div>
        </Flex.Item>
      )}
    </Flex>
  );
};
